import { useEffect, useRef, useState } from 'react'
import { queuePromptDisplay, serverStatusText } from './lib/jobPresentation'
import type {
  QueuePromptSummary,
  ServerDefinition,
  ServerRuntime,
} from './types'

type ServerStatusStripProps = {
  servers: ServerDefinition[]
  runtimes: Record<string, ServerRuntime>
  target?: string
  emptyLabel?: string
}

const queueRows = (
  rows: QueuePromptSummary[] | undefined,
  count: number,
  prefix: string,
): QueuePromptSummary[] =>
  Array.from({ length: count }, (_, index) => rows?.[index] ?? {
    promptId: `${prefix}-unknown-${index}`,
  })

export function ServerStatusStrip({
  servers,
  runtimes,
  target,
  emptyLabel = 'Settings › Servers で追加',
}: ServerStatusStripProps) {
  const [openServerId, setOpenServerId] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const openServer = servers.find((server) => server.id === openServerId)
  const openRuntime = openServer ? runtimes[openServer.id] : undefined
  const runningJobs = queueRows(openRuntime?.runningJobs, openRuntime?.running ?? 0, 'running')
  const pendingJobs = queueRows(openRuntime?.pendingJobs, openRuntime?.pending ?? 0, 'pending')

  useEffect(() => {
    if (!openServerId) return
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpenServerId('')
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenServerId('')
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [openServerId])

  useEffect(() => {
    if (openServerId && !servers.some((server) => server.id === openServerId)) {
      setOpenServerId('')
    }
  }, [openServerId, servers])

  return (
    <div className="server-status-control" ref={rootRef}>
      <div className="server-strip" aria-label="Server status">
        {servers.length === 0 && <span className="server-strip-empty">{emptyLabel}</span>}
        {servers.map((server) => {
          const runtime = runtimes[server.id]
          const status = runtime?.status ?? 'disconnected'
          const statusText = serverStatusText(runtime)
          const expanded = openServerId === server.id
          return <button
            type="button"
            key={server.id}
            className={`server-chip is-${status} ${target === server.id ? 'is-target' : ''}`}
            title={runtime?.error || `${server.name}・${statusText}・クリックで Queue を表示`}
            aria-label={`${server.name} queue details`}
            aria-expanded={expanded}
            onClick={() => setOpenServerId((current) => current === server.id ? '' : server.id)}
          ><span className="server-dot" /> <strong>{server.name}</strong><small>{statusText}</small></button>
        })}
      </div>
      {openServer && <div className="server-queue-popover" role="dialog" aria-label={`${openServer.name} Queue`}>
        <header>
          <strong>{openServer.name}</strong>
          <small>{serverStatusText(openRuntime)}</small>
        </header>
        {runningJobs.length > 0 && <section>
          <h4>実行中</h4>
          <ul>{runningJobs.map((job) => <li key={job.promptId}>
            <strong>{job.submittedBy || 'Unknown / ComfyUI'}</strong>
            <small>{queuePromptDisplay(job)}</small>
          </li>)}</ul>
        </section>}
        {pendingJobs.length > 0 && <section>
          <h4>待機中（{pendingJobs.length}）</h4>
          <ul>{pendingJobs.map((job) => <li key={job.promptId}>
            <strong>{job.submittedBy || 'Unknown / ComfyUI'}</strong>
            <small>{queuePromptDisplay(job)}</small>
          </li>)}</ul>
        </section>}
        {!runningJobs.length && !pendingJobs.length && <p>現在 Queue は空です。</p>}
      </div>}
    </div>
  )
}
